class Request {
  constructor (
    id,
    receiverId,
    senderId,
    studentId,
    status,
    createdAt,
    updatedAt
  ) {
    if (!(typeof id === 'string')) {
      throw new TypeError(`The type of 'id' must be String. Actual: ${typeof id}`)
    }
    if (!(typeof receiverId === 'string')) {
      throw new TypeError(`The type of 'senderId' must be String. Actual: ${typeof receiverId}`)
    }
    if (!(typeof senderId === 'string')) {
      throw new TypeError(`The type of 'senderId' must be String. Actual: ${typeof senderId}`)
    }
    if (!(typeof studentId === 'string')) {
      throw new TypeError(`The type of 'studentId' must be String. Actual: ${typeof studentId}`)
    }
    if (!(Object.values(RequestStatus).includes(status))) {
      throw new Error(`'status' must be an value of 'RequestStatus'. But ${status} is not.`)
    }
    this.id = id
    this.receiverId = receiverId
    this.senderId = senderId
    this.studentId = studentId
    this.status = status
    this.createdAt = createdAt
    this.updatedAt = updatedAt
  }

  toObject() {
    return {
      id: this.id,
      data: {
        receiverId: this.receiverId,
        senderId: this.senderId,
        studentId: this.studentId,
        status: this.status,
        createdAt: this.createdAt,
        updatedAt: this.updatedAt
      }
    }
  }

  isStatus(status) {
    if (!(typeof status === "string")) {
      throw new TypeError(`The type of 'status' must be String. Actual: ${typeof status}`)
    }
    return (this.status === status)
  }

  hasId(id) {
    if(!(typeof id === "string")) {
      throw new TypeError(`The type of 'id' must be String. Actual: ${typeof id}` )
    }
    return (this.id === id)
  }

  hasStudentId(studentId) {
    if (!(typeof studentId === "string")) {
      throw new TypeError(`The type of 'studentId' must be String. Actual: ${typeof studentId}`)
    }
    return (this.studentId === studentId)
  }

  hasSenderId(senderId) {
    if (!(typeof senderId === "string")) {
      throw new TypeError(`The type of 'senderId' must be String. Actual: ${typeof senderId}`)
    }
    return (this.senderId === senderId)
  }

  is(other) {
    if (!(other instanceof Request)) {
      throw new TypeError(`'other' must be an instance of [object Request]. Actual: ${typeof other}`)
    }
    return (this.id === other.id)
  }

  isInRequest() {
    return (this.status === RequestStatus.inRequest)
  }

  isAccepted() {
    return (this.status === RequestStatus.accepted)
  }

  isDenied() {
    return (this.status === RequestStatus.denied)
  }

  toAccepted() {
    this.status = RequestStatus.accepted
    return this
  }

  toDinied() {
    this.status = RequestStatus.denied
    return this
  }
}

class Requests {
  constructor(requests) {
    this.requests = requests
  }

  get() {
    return this.requests
  }

  length() {
    return this.requests.length
  }

  toObjects() {
    return this.requests.map(request => {
      return request.toObject()
    })
  }

  register(request) {
    if (!(request instanceof Request)) {
      throw new TypeError(`'request' must be an instance of [object Request]. Actual: ${typeof request}`)
    }
    this.requests.push(request)
    return this
  }

  remove(request) {
    if (!(request instanceof Request)) {
      throw new TypeError(`'request' must be an instance of [object Request]. Actual: ${typeof request}`)
    }
    this.requests.splice(this.requests.findIndex(r => {
      return r.is(request)
    }), 1)
    return this
  }

  filterByStudentId(studentId) {
    if (!(typeof studentId === "string")) {
      throw new TypeError(`The type of 'studentId' must be String. Actual: ${typeof studentId}`)
    }
    const arr = this.requests.filter(request => {
      return request.hasStudentId(studentId)
    })
    return new Requests(arr)
  }

  filterBySenderId(senderId) {
    if (!(typeof senderId === "string")) {
      throw new TypeError(`The type of 'senderId' must be String. Actual: ${typeof senderId}`)
    }
    const arr = this.requests.filter(request => {
      return request.hasSenderId(senderId)
    })
    return new Requests(arr)
  }

  find(id) {
    if(!(typeof id === "string")) {
      throw new TypeError(`The type of 'id' must be String. Actual: ${typeof id}`)
    }
    return this.requests[this.requests.findIndex(r => r.hasId(id))]
  }

  findBySenderId(senderId) {
    if (!(typeof senderId === "string")) {
      throw new TypeError(`The type of 'senderId' must be String. Actual: ${typeof senderId}`)
    }
    const filtered = this.filterBySenderId(senderId)
    if (filtered.length() === 1) {
      return filtered.requests[0]
    } else {
      throw Error(`Filtered requests must have only a request. Actual: ${filtered.length()}`)
    }
  }

  map(callback) {
    return this.requests.map(request => callback(request))
  }

  senderIds() {
    return this.requests.map(request => request.senderId)
  }

  getStudentIds() {
    return this.requests.map(request => request.studentId)
  }

  notTeaching(teacher) {
    const requests = this.requests.filter(request => !teacher.studentIds.includes(request.studentId))
    return new Requests(requests)
  }

}

const RequestStatus = {
  inRequest: "IN_REQUEST",
  accepted: "ACCEPTED",
  denied: "DENIED"
}

Object.freeze(RequestStatus)

export { Request, Requests, RequestStatus }